import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import TemplateHtml from '../../../html/arcade.html';
import Alerts from '../../alerts/alerts';
import Analytics from '../../analytics';
import BaseView from '../../baseView';
import Environment from '../../environment';
import { ENGINES } from '../../gameFactory';
import InfoButton, { ATTRIBUTES as INFO_ATTRIBUTES } from '../../infoButton';
import ArcadeSkins from '../../manager/arcadeSkinManager';
import AudioManager from '../../manager/audioManager';
import AppAuthManager from '../../manager/hwptvAppAuthManager';
import InputManager from '../../manager/inputManager';
import Verizon from '../../platform/verizon';
import Router from '../../router';
import FontLoader from '../../util/fontLoader';
import PerformanceStats from '../../util/performanceStats/performanceStats';
import { arrayPromiseChain } from '../../util/utilities';
import { leaderboardAdventToNodeGames } from '../advent/adventLeaderboardUtils';
import TeamChooser from '../teamChooser/teamChooser';
import ArcadeModel, { ATTRIBUTES, EVENTS } from './arcadeModel';
import Header from './header';

/**
 * @typedef {import('./arcadeModel').ArcadeModelOptions} ArcadeModelOptions
 * @typedef {import('../../baseView').BaseViewOptions} BaseViewOptions
 * @typedef {ArcadeModelOptions & BaseViewOptions} ArcadeViewOptions
 */
export default class Arcade extends BaseView {
	get CN() {
		return Arcade.name;
	}

	get template() {
		return _.template(TemplateHtml);
	}

	get isReady() {
		this.logger.log('[isReady]');
		return this.readyPromise;
	}

	id() {
		return 'arcade';
	}

	/**
	 * @param {ArcadeViewOptions} options
	 */
	constructor(options) {
		super(options);

		this.logger.log('[constructor]', { options });
	}

	/**
	 * @param {ArcadeViewOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		super.initialize(options);
		this.configureForEnvironment();

		this.readyPromise = new Promise((resolve, reject) => {
			this.onReadyResolve = resolve;
			this.onReadyReject = reject;
		});

		this.render();

		this.arcadeState = new ArcadeModel(options).on(
			EVENTS.initialized,
			this.onDataLoaded.bind(this),
		);
	}

	configureForEnvironment() {
		this.logger.log(`[${this.configureForEnvironment.name}]`);

		// Configure tv mode
		if (Environment.isSetTopBox() || Environment.isMTheory()) {
			this.addArcadeSkinId('tv');
		}

		if (!Environment.isProd()) {
			const stats = new PerformanceStats();
			requestAnimationFrame(function updateStats() {
				stats.update();
				requestAnimationFrame(updateStats);
			});
			$('body').append(stats.domElement);

			InputManager.on('0', () => {
				const activeTag = document.activeElement?.tagName || '';
				if (/^(input|textarea|select|option)$/i.test(activeTag)) {
					// Prevent reloading if we're using an input field
					return;
				}

				const currentUrl = new URL(window.location.href);
				currentUrl.searchParams.set('cacheBuster', Date.now().toString());
				window.location.href = currentUrl.toString();
			});
		}
	}

	onDataLoaded() {
		this.logger.log(`[${this.onDataLoaded.name}]`);

		const { arcadeName, accountVariant, audioManager, helpPopUp } = this.arcadeState.attributes;

		// Set Analytics prefix so auth analytics works correctly
		Analytics.setArcade(arcadeName);

		if (audioManager) {
			AudioManager.initialize(audioManager);
		}
		if (helpPopUp) {
			InfoButton.initialize(helpPopUp);
		}

		return Analytics.isReady
			.then(() => AppAuthManager.initialize({ accountVariant }))
			.then(() => this.addSkin())
			.then(() => this.entitlementCheck())
			.then(() => FontLoader.preloadDocumentFonts())
			.then(() => this.addHeader())
			.then(() => this.processLeaderboards())
			.then(() => this.registerRoutes())
			.then(() => this.insertInDom())
			.then(() => {
				this.logger.info(`[Initialization Complete]`);
				return Analytics.sendEvent('ArcadeLaunch', { engine: 'Arcade' });
			})
			.then(() => this.trigger(EVENTS.initialized))
			.then(
				() => this.onReadyResolve(this),
				(error) => {
					this.logger.error(error);
					this.onReadyReject(error);
				},
			);
	}

	addSkin() {
		this.logger.log(`[${this.addSkin.name}]`);

		const { [ATTRIBUTES.skin]: className = '', [ATTRIBUTES.skinModules]: skins = '' } =
			this.arcadeState.pick([ATTRIBUTES.skin, ATTRIBUTES.skinModules]);

		this.logger.debug({ className, skins });

		if (className) {
			this.addArcadeSkinId(className);
		}

		return Promise.resolve()
			.then(() => {
				// Check for any skins to be loaded
				if (skins) {
					return Promise.all(
						skins
							.split(/[,\s]+/) // Split ot comma, space, or both to separate names of multiple skins
							.map((skin) => skin.trim())
							.map((skin) => ArcadeSkins.loadSkin(skin)),
					);
				}

				return Promise.resolve();
			})
			.then(() => {
				const teamChooserPage = this.arcadeState
					.get(ATTRIBUTES.pages)
					.find(({ type }) => type === ENGINES.teamChooser);

				if (teamChooserPage?.gameId) {
					// Apply any Team Chooser skins that have been chosen
					return new Promise((resolve) => {
						const teamChooser = new TeamChooser(teamChooserPage);
						teamChooser.once(EVENTS.initialized, () => {
							if (teamChooser.chosenTeam) {
								this.logger.log({ chosenTeam: teamChooser.chosenTeam });
								this.addArcadeSkinId(teamChooser.chosenTeam);
							}
							resolve();
						});
					});
				}

				return Promise.resolve();
			});
	}

	/**
	 * Centralized function to add a classname that alters the visuals of the arcade.
	 * @param {string} skinClassName The classname to add.
	 */
	addArcadeSkinId(skinClassName) {
		this.logger.log(`[${this.addArcadeSkinId.name}]`, { skinClassName });

		$('body').addClass(skinClassName);
	}

	addHeader() {
		this.logger.log(`[${this.addHeader.name}]`);

		return new Promise((resolve) => {
			if (!this.arcadeState.get(ATTRIBUTES.disableHeader)) {
				this.header = new Header({ pages: this.arcadeState.get(ATTRIBUTES.pages) });
				// Header is added after body to facilitate ~ css selector for styling specific pages
				this.$el.append(this.header.el);
			}
			resolve();
		});
	}

	/**
	 * Checks all leaderboards pages for nested games such as Advent and replaces them with their
	 * 'nested' games if neccesary.
	 * @returns {Promise<any>}
	 */
	processLeaderboards() {
		this.logger.log(`[${this.processLeaderboards.name}]`);

		const leaderboardPages = this.arcadeState
			.get(ATTRIBUTES.pages)
			.filter(({ type }) => type === ENGINES.leaderboards);

		return arrayPromiseChain(leaderboardPages, leaderboardAdventToNodeGames);
	}

	registerRoutes() {
		this.logger.log(`[${this.registerRoutes.name}]`);

		return new Promise((resolve) => {
			const pages = this.arcadeState.get(ATTRIBUTES.pages);
			if (!pages) {
				this.logger.warn(
					'Arcade data has no pages, ensure data column is using "pages.id".',
				);
			}

			pages.forEach((page, index) => {
				const { route, id, engine } = page || {};

				if (route) {
					const pageRoute = index > 0 ? route : `*${route}`;
					this.logger.debug('[Registering Page]', `[${pageRoute}]`, page);
					Router.registerRoute(pageRoute, id, engine, page);
				} else {
					this.logger.warn(`[No Route provided for page entry]`, { page });
				}
			});

			Router.on('route', this.onRouteChange.bind(this));

			resolve();
		});
	}

	entitlementCheck() {
		this.logger.log(`[${this.entitlementCheck.name}]`);

		const entitlementEntity = this.arcadeState.get(ATTRIBUTES.entitlementEntity);
		if (entitlementEntity) {
			if (Environment.isVerizonSetTopBox()) {
				Verizon.isSubscriber(entitlementEntity).then(() =>
					this.addArcadeSkinId('subscriber'),
				);
			} else {
				this.logger.info(
					`Platform does not support entitlment check for "${entitlementEntity}`,
				);
			}
		}
	}

	insertInDom() {
		this.logger.log(`[${this.insertInDom.name}]`);

		const { body, referrer } = document;
		this.dismissElement(body)
			.then(() => {
				body.classList.remove('splashed');
				$(body).append(Alerts.el, InfoButton.el, this.el);

				Backbone.history.start();

				if (window.parent && referrer) {
					window.parent.postMessage('finishedLoading', referrer);
				}
			})
			.then(() => this.displayElement(body));
	}

	onRouteChange(pageId) {
		this.logger.log(`[${this.onRouteChange.name}]`, { pageId });

		// Defaulting to an empty string will cause the background image to unset returning it to the arcade default background
		const { pageBg = '', showHelpPopUp = false } = this.arcadeState
			.get(ATTRIBUTES.pages)
			.find(({ id }) => id === pageId);

		const $bg = this.$('#page-bg');
		/** @type {HTMLVideoElement} */
		const video = $bg.find('video').get(0);
		const currentVideoSrc = video.src;

		if (/\.mp4/u.test(pageBg)) {
			// Video background
			$bg.css({ backgroundImage: '' });
			if (pageBg !== currentVideoSrc) {
				video.src = pageBg;
				this.playVideo(video);
			}
		} else if (/\.(jpg|png)/.test(pageBg)) {
			// Image background
			this.cleanUpVideo(video);
			$bg.css({ backgroundImage: `url("${pageBg}")` });
		} else {
			this.cleanUpVideo(video);
			$bg.css({ backgroundImage: '' });
		}

		if (showHelpPopUp && !InfoButton[INFO_ATTRIBUTES.isEnabled]) {
			InfoButton.enable();
		} else if (!showHelpPopUp && InfoButton[INFO_ATTRIBUTES.isEnabled]) {
			InfoButton.disable();
		}
	}
}
